import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/store/shared/supabase";
import { useSession } from "@/session/SessionProvider";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import {
  countUnreadNotifications,
  fetchNotifications,
  generateEssaiNotifications,
  generateImpayeNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "./notificationsData";
import type { NotificationItem } from "./types";

interface NotificationsContextValue {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  /** Vrai si l'abonnement temps réel est actif (Supabase Realtime). */
  realtime: boolean;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

/**
 * Notifications de l'utilisateur connecté.
 *
 * - une seule requête de liste + un seul compteur de non-lues pour tout l'espace ;
 * - un unique canal Realtime (filtré sur `compte_id = compte connecté`), nettoyé
 *   au démontage ;
 * - les policies RLS s'appliquent aussi au Realtime : aucun risque de fuite
 *   entre comptes ou organisations.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const compteId = session?.id || null;

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [realtime, setRealtime] = useState(false);

  const debounceRef = useRef<number | null>(null);

  const load = useCallback(
    async (silent = false) => {
      if (!compteId) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      if (!silent) setLoading(true);
      setError(null);
      try {
        // Reflets best-effort : impayés de l'agence et paliers de fin d'essai
        // (J-30/J-7/J-1). Idempotents côté serveur, no-op hors périmètre concerné.
        // Un échec ici ne doit jamais empêcher l'affichage des notifications.
        await Promise.all([
          generateImpayeNotifications().catch(() => undefined),
          generateEssaiNotifications().catch(() => undefined),
        ]);

        const [list, unread] = await Promise.all([
          fetchNotifications(),
          countUnreadNotifications(),
        ]);
        setNotifications(list);
        setUnreadCount(unread);
      } catch (caught) {
        setError("Le chargement des notifications a échoué.");
        dispatchDataError({
          scope: "Notifications",
          message: "Impossible de charger vos notifications.",
          technical: describeSupabaseError(caught),
        });
        console.error("[Notifications] chargement Supabase échoué", caught);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [compteId]
  );

  // Chargement initial + à chaque changement de compte.
  useEffect(() => {
    void load(false);
  }, [load]);

  // Abonnement temps réel (un seul canal, nettoyé au démontage).
  useEffect(() => {
    if (!compteId) {
      setRealtime(false);
      return;
    }
    const scheduleReload = () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        void load(true);
      }, 400);
    };

    const channel = supabase
      .channel(`notifications:${compteId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `compte_id=eq.${compteId}`,
        },
        scheduleReload
      )
      .subscribe((status) => {
        setRealtime(status === "SUBSCRIBED");
      });

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      setRealtime(false);
      void supabase.removeChannel(channel);
    };
  }, [compteId, load]);

  const markRead = useCallback(
    async (id: string) => {
      const target = notifications.find((item) => item.id === id);
      if (!target || target.lu) return;
      // Mise à jour optimiste puis synchronisation serveur.
      setNotifications((prev) => prev.map((item) => (item.id === id ? { ...item, lu: true } : item)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      try {
        await markNotificationRead(id);
      } catch (caught) {
        void load(true);
        dispatchDataError({
          scope: "Notifications",
          message: "Impossible de marquer cette notification comme lue.",
          technical: describeSupabaseError(caught),
        });
      }
    },
    [notifications, load]
  );

  const markAllRead = useCallback(async () => {
    if (unreadCount === 0) return;
    setNotifications((prev) => prev.map((item) => ({ ...item, lu: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch (caught) {
      void load(true);
      dispatchDataError({
        scope: "Notifications",
        message: "Impossible de marquer les notifications comme lues.",
        technical: describeSupabaseError(caught),
      });
    }
  }, [unreadCount, load]);

  const refresh = useCallback(async () => {
    await load(false);
  }, [load]);

  const value = useMemo(
    () => ({ notifications, unreadCount, loading, error, realtime, refresh, markRead, markAllRead }),
    [notifications, unreadCount, loading, error, realtime, refresh, markRead, markAllRead]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications doit être utilisé à l'intérieur de NotificationsProvider");
  }
  return context;
}